import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTable } from '@angular/material/table';
import { from, Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { WebSocketNotificationType } from 'src/app/core/websocket/model';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';

import { UserPreferencesAccessCheckService } from './user-preferences-access-check.service';

@Component({
  selector: 'app-user-preferences-access-check',
  templateUrl: './user-preferences-access-check.component.html',
  styleUrls: ['../../user-preferences/shared/user-preferences.scss', './user-preferences-access-check.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserPreferencesAccessCheckComponent implements OnInit, OnDestroy {
  clientSessionId = '';

  dataSource = [];
  displayedColumns = ['name', 'status', 'error'];
  maxHeight = 500;

  @ViewChild(MatTable) table: MatTable<any>;

  /**
   * Allows to have a full responsive table by taking the available height. The overflow of the table will be visible by using a scroll.
   * @remarks 200 considers the headers (48px) and the filters (152px). As the target is the routed component, the header is ignored.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.maxHeight = Math.max(document.getElementById('right-side-content').clientHeight - 136, 500);
  }

  constructor(
    private userPreferencesAccessCheckService: UserPreferencesAccessCheckService,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('XHRStatsFactory') xhrStatsService: any,
    private webSocketService: WebSocketService
  ) {
    topToolbarService.setTitleTranslation('status-check/title', null);
    topToolbarService.setButton('status-check/button/name', this.startCheck1, null);

    this.clientSessionId = xhrStatsService.clientSessionId();
    this.onResize();
  }

  ngOnInit() {
    this.onResize();
  }

  startCheck1 = () => {
    this.topToolbarService.updateButtonDisable(true, null);
    const check = this.getPendingCheck('alcmeon-app');
    this.userPreferencesAccessCheckService
      .getAlcmeonApp()
      .pipe(finalize(() => (this.dataSource = this.dataSource.concat([check]))))
      .subscribe({
        next: () => {
          check.status = 'status-check/status/ok';
          this.startCheck2();
        },
        error: () => {
          check.status = 'status-check/status/fail';
          check.error = 'status-check/error/alcmeon-app';
          this.topToolbarService.updateButtonDisable(false, null);
        },
      });
  };

  private startCheck2() {
    const check = this.getPendingCheck('alcmeon-websocket');
    from(this.wsPing())
      .pipe(finalize(() => (this.dataSource = this.dataSource.concat([check]))))
      .subscribe({
        next: () => {
          check.status = 'status-check/status/ok';
          this.startCheck3();
        },
        error: () => {
          check.status = 'status-check/status/fail';
          check.error = 'status-check/error/ws';
          this.topToolbarService.updateButtonDisable(false, null);
        },
      });
  }

  private startCheck3() {
    const check = this.getPendingCheck('alcmeon-latency');
    this.wsPings(10, [])
      .pipe(
        finalize(() => {
          this.dataSource = this.dataSource.concat([check]);
          this.topToolbarService.updateButtonDisable(false, null);
        })
      )
      .subscribe({
        next: avg => {
          if (avg >= 2.0) {
            check.status = 'status-check/status/very_slow';
          } else if (avg >= 1.0) {
            check.status = 'status-check/status/slow';
          } else {
            check.status = 'status-check/status/fast';
          }
        },
        error: () => (check.status = 'status-check/status/fail'),
      });
  }

  private wsPing() {
    const expectedUuid = this.userPreferencesAccessCheckService.getUuid();
    const start = new Date().getTime();
    let subscription: Subscription;
    const wsPromise = new Promise((resolve, reject) => {
      setTimeout(() => reject(), 5000);
      subscription = this.webSocketService.message$
        .pipe(filter((event: any) => event.type === WebSocketNotificationType.AccesCheck))
        .subscribe({
          next: event => {
            if (event.data.uuid === expectedUuid) {
              resolve(1);
            }
          },
        });
    });

    const post = this.userPreferencesAccessCheckService.postWSNotification({ uuid: expectedUuid }).toPromise();

    return Promise.all([wsPromise, post]).then(() => {
      subscription?.unsubscribe();
      const end = new Date().getTime();
      const delay = (end - start) / 1000;
      return Promise.resolve(delay);
    });
  }

  private wsPings(n: number, delays: number[]) {
    if (n === 1) {
      return this.wsPing().then(delay => {
        const sum = delays.concat([delay]).reduce((acc, cur) => acc + cur, 0);
        const avg = sum / (delays.length + 1);
        return avg;
      });
    }
    return from(this.wsPing().then(delay => this.wsPings(n - 1, delays.concat([delay]))));
  }

  private getPendingCheck(name) {
    return {
      name: name,
      status: 'status-check/status/pending',
      error: '',
    };
  }

  ngOnDestroy(): void {
    this.topToolbarService.clearButtons();
  }
}
